import type { ColumnsType } from 'antd/es/table';

import type { Sort } from '@/utils/types';

type EnhanceColumnsParams<T> = {
  columns: ColumnsType<T>;
  sort?: Sort;
  hiddenColumns?: string[];
  handleSort: (sortBy: string) => void;
};

export const enhanceColumns = <T>({
  columns,
  sort,
  hiddenColumns,
  handleSort
}: EnhanceColumnsParams<T>): ColumnsType<T> =>
  columns.map(col => {
    const children = (col as Record<string, unknown>).children as
      ColumnsType<T> | undefined;

    if (children?.length) {
      const enhancedChildren = children.map(child => ({
        ...child,
        ...(child.sorter &&
          getSortProps(child.key as string, sort, handleSort)),
        hidden: child.hidden || hiddenColumns?.includes(child.key as string)
      }));

      const allChildrenHidden = enhancedChildren.every(child => child.hidden);

      return {
        ...col,
        children: enhancedChildren,
        hidden: col.hidden || allChildrenHidden
      };
    }

    return {
      ...col,
      ...(col.sorter && getSortProps(col.key as string, sort, handleSort)),
      hidden: col.hidden || hiddenColumns?.includes(col.key as string)
    };
  });

const getSortProps = (
  key: string,
  sort: Sort | undefined,
  handleSort: (sortBy: string) => void
) => ({
  sortOrder: getSortOrder(key, sort),
  onHeaderCell: () => ({ onClick: () => handleSort(key) })
});

const getSortOrder = (
  key: string,
  sort?: Sort
): 'ascend' | 'descend' | undefined => {
  if (sort?.sort_by !== key) return undefined;
  return sort.sort_order === 'asc' ? 'ascend' : 'descend';
};
